import { Link } from 'react-router-dom';
import { Button } from '@heroui/react';

import TimeAgo from './TimeAgo';
import UserBadge from './UserBadge';
import { ACTIVITY, MENTIONS } from '../../i18n/el';

/**
 * Μία γραμμή ροής δραστηριότητας (ΛΑ-21, ΛΑ-23, ΜΛΑ-04).
 *
 * **Μία γραμμή είναι μία ΕΝΕΡΓΕΙΑ, όχι μία εγγραφή.** Ο εξυπηρετητής ομαδοποιεί
 * πριν σελιδοποιήσει, οπότε εδώ φτάνει έτοιμη ομάδα με πλήθος — μια αποσύνδεση
 * πόρου από τρεις συλλογές είναι **μία** γραμμή, όχι τρεις σχεδόν πανομοιότυπες.
 *
 * Το συστατικό είναι **κοινό** και όχι τοπικό, γιατί εμφανίζεται σε τρεις θέσεις
 * με το ίδιο ακριβώς σχήμα: στη ροή της συλλογής, στον κατάλογο ειδοποιήσεων, και
 * στο ιστορικό ενός πόρου. Τρία αντίγραφα θα απέκλιναν στη διατύπωση, που εδώ
 * είναι το ουσιαστικό: το «άλλαξε ΑΛΛΟΥ» πρέπει να διαβάζεται διαφορετικά από το
 * «άλλαξε ΕΔΩ» σε **κάθε** οθόνη που το δείχνει.
 *
 * ΜΛΑ-04: η **συνεισφορά κάθε μέλους** γίνεται ορατή με πρόσωπο και χρόνο, όχι
 * με απρόσωπη καταγραφή.
 *
 * @param entry     Η ομάδα, όπως την επιστρέφει το API.
 * @param onMarkRead Αν δοθεί, εμφανίζεται η ενέργεια σήμανσης. Χωρίς αυτό η
 *                   γραμμή είναι μόνο αναγνώσιμη — έτσι το ίδιο συστατικό
 *                   εξυπηρετεί και τη ροή, που δεν έχει κατάσταση ανάγνωσης.
 * @param error     Μήνυμα αποτυχίας **αυτής** της γραμμής.
 */
/**
 * Η φράση σπασμένη στη θέση του αντικειμένου.
 *
 * ⚠ **Η προηγούμενη μορφή έβαζε το όνομα ΠΑΝΤΟΤΕ στο τέλος**, και για επτά
 * γεγονότα η φράση τελείωνε σε **άλλο** ουσιαστικό: «πρόσθεσε μέλος στη συλλογή
 * «annamaria»» διαβάζεται ως όνομα της συλλογής. Δεν ήταν επτά κακές
 * διατυπώσεις αλλά **μία παραδοχή** που έτυχε να ισχύει στις υπόλοιπες.
 *
 * Καθαρή συνάρτηση και εξαγόμενη, ώστε ο ισχυρισμός να την ασκεί χωρίς απόδοση:
 * το ζητούμενο είναι **η σειρά των τριών μερών**, που δεν φαίνεται σε σήμανση.
 */
export function phraseParts(template, label) {
    const text = String(template ?? '');
    const at = text.indexOf('{}');

    // Φράση χωρίς θέση: το γεγονός δεν φέρνει όνομα. Αν παρ' όλα αυτά υπάρχει
    // όνομα, **δεν κολλιέται στο τέλος** — αυτό ήταν το σφάλμα. Πηγαίνει σε
    // παρένθεση, όπου δεν μπορεί να διαβαστεί ως συμπλήρωμα της τελευταίας λέξης.
    if (at === -1) return { before: text, label: label ?? null, after: '', isTrailing: true };

    return {
        before: text.slice(0, at).trimEnd(),
        label: label ?? null,
        after: text.slice(at + 2).trimStart(),
        isTrailing: false
    };
}

export default function ActivityItem({ entry, onMarkRead = null, isMarking = false, error = '' }) {
    // ⚠ **Β2: Η ΑΟΡΙΣΤΗ ΜΟΡΦΗ, ΟΤΑΝ ΤΟ ΟΝΟΜΑ ΔΕΝ ΗΡΘΕ.** Υπάρχουν φράσεις των
    // οποίων το όνομα το κρίνει ο εξυπηρετητής **ανά αναγνώστη**: αν ο αναγνώστης
    // δεν δικαιούται τον πόρο, το πεδίο λείπει. Χωρίς εναλλακτική διατύπωση η
    // πρόταση θα σταματούσε στη μέση — «σχολίασε τον πόρο» και τίποτα.
    const named = entry.object?.title ?? entry.object?.username ?? null;
    const phrase = (named === null && ACTIVITY.verbsWithoutName?.[entry.verb])
        || ACTIVITY.verbs[entry.verb]
        || entry.verb;
    const icon = ACTIVITY.icons[entry.objectType] ?? 'fa-solid fa-circle-info';
    const isUnread = entry.unread > 0;

    // Ο τίτλος του αντικειμένου όταν υπάρχει. Το φορτίο είναι η κατάσταση **της
    // στιγμής** του γεγονότος, οπότε ένας πόρος που διαγράφηκε εξακολουθεί να
    // έχει όνομα εδώ — που είναι όλος ο λόγος που αποθηκεύεται.
    const label = named;
    const parts = phraseParts(phrase, label);

    // Ο προορισμός του ονόματος, όταν υπάρχει. Έρχεται **μαζί** με τον τίτλο από
    // τον ίδιο φράχτη ορατότητας, οπότε δεν μπορεί να υπάρχει το ένα χωρίς το
    // άλλο — και το `collection` είναι απαραίτητο, γιατί ο μόνιμος σύνδεσμος
    // είναι `/collections/:id?resource=` και όχι δική του οθόνη (D15).
    const target = entry.object?.resourceId && entry.collection
        ? `/collections/${entry.collection}?resource=${entry.object.resourceId}`
        : null;

    // **`||` και όχι `??`**, για τον ίδιο λόγο με το `UserBadge`: ο εξυπηρετητής
    // στέλνει τα προαιρετικά πεδία ως **κενή συμβολοσειρά** και όχι ως απουσία,
    // οπότε το `??` θα άφηνε το όνομα κενό χωρίς κανένα σφάλμα.
    const actorName = entry.actor?.fullName || entry.actor?.username || '';
    // Το όνομα χρήστη **παραλείπεται όταν ταυτίζεται** με το εμφανιζόμενο όνομα:
    // ο χρήστης χωρίς ονοματεπώνυμο θα διαβαζόταν «alice (@alice)». Ο κανόνας
    // είναι ο ίδιος με εκείνον του `UserBadge` — και χρειάστηκε να γραφτεί και
    // εδώ ακριβώς επειδή η στήλη κειμένου εκείνου δεν χρησιμοποιείται. Το βρήκε
    // δοκιμή που γράφτηκε **μαζί** με τη διόρθωση, όχι μετά.
    const handle = entry.actor?.username ? `@${entry.actor.username}` : null;
    const actorHandle = handle && handle !== `@${actorName}` ? handle : null;

    return (
        <li
            className={[
                'flex gap-3 rounded-lg border border-border px-3 py-3',
                isUnread ? 'border-accent/40 bg-accent-soft/30' : 'bg-surface'
            ].join(' ')}
        >
            <span
                className="mt-1 flex size-8 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent-soft-foreground"
                aria-hidden="true"
            >
                <i className={icon} />
            </span>

            <div className="flex min-w-0 flex-1 flex-col gap-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    {/* ⚠ **Ο δράστης ΜΕΣΑ στην πρόταση, και μόνο εδώ.**
                        Το `UserBadge` στοιβάζει το όνομα χρήστη **κάτω** από το
                        ονοματεπώνυμο — σωστά, γιατί εκεί που χρησιμοποιείται αλλού
                        είναι **ταυτότητα** και όχι υποκείμενο πρότασης. Εδώ όμως ο
                        δράστης είναι το υποκείμενο: με δύο γραμμές, το ρήμα και το
                        αντικείμενο ευθυγραμμίζονταν στο **κέντρο** ενός στοιχείου
                        διπλού ύψους και η πρόταση έσπαγε στη μέση.

                        Το συστατικό **δεν αλλάζει**: χρησιμοποιείται με κρυμμένη τη
                        στήλη κειμένου του — ακριβώς η παράμετρος που υπάρχει για την
                        κεφαλίδα — ώστε ο κύκλος του προσώπου να μείνει ένας και
                        μοναδικός (ΜΛΑ-04), και το κείμενο να γραφτεί εδώ σε μία
                        γραμμή. */}
                    <UserBadge user={entry.actor} size="sm" textClassName="hidden" />
                    {entry.actor && (
                        <span className="text-sm">
                            <span className="font-medium">{actorName}</span>
                            {actorHandle && <span className="text-muted">{` (${actorHandle})`}</span>}
                        </span>
                    )}
                    {/* Ο χρήστης που δεν υπάρχει πια δεν αφήνει κενή θέση: η
                        εγγραφή παραμένει έγκυρο ίχνος και χωρίς λογαριασμό. */}
                    {!entry.actor && <span className="text-sm text-muted">{ACTIVITY.unknownActor}</span>}
                    {/* Τρία μέρη, με το αντικείμενο **στη θέση που ορίζει η ίδια
                        η φράση**. Το κενό στοιχείο δεν αποδίδεται καθόλου, ώστε
                        να μη μείνει διάστημα πριν από τελεία ή παρένθεση. */}
                    <span className="text-sm text-muted">{parts.before}</span>
                    {parts.label && (
                        // ⚠ **Β2: ΣΥΝΔΕΣΜΟΣ ΟΤΑΝ ΥΠΑΡΧΕΙ ΠΡΟΟΡΙΣΜΟΣ, ΚΕΙΜΕΝΟ ΑΛΛΙΩΣ.**
                        // Το όνομα του πόρου χωρίς τρόπο να τον ανοίξεις ζητά από
                        // τον αναγνώστη να τον ψάξει μόνος του — ακριβώς η δουλειά
                        // που η γραμμή υπάρχει για να του γλιτώσει. Ο προορισμός
                        // έρχεται **μαζί** με το όνομα, από τον ίδιο φράχτη: όπου
                        // λείπει, λείπει και το όνομα, οπότε η συνθήκη δεν μπορεί
                        // να δώσει σύνδεσμο προς κάτι που δεν επιτρέπεται.
                        //
                        // Το `truncate` **δεν** μπαίνει στον σύνδεσμο: τίτλος
                        // κομμένος με «…» μέσα σε σύνδεσμο διαβάζεται ως σπασμένος
                        // προορισμός. Η γραμμή αναδιπλώνεται ήδη.
                        target ? (
                            <Link
                                to={target}
                                className="text-sm font-medium underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                            >
                                {`«${parts.label}»`}
                            </Link>
                        ) : (
                            <span className="truncate text-sm font-medium">
                                {parts.isTrailing ? `(${parts.label})` : `«${parts.label}»`}
                            </span>
                        )
                    )}
                    {parts.after && <span className="text-sm text-muted">{parts.after}</span>}

                    {/* ⚠ **D44: ΓΙΑΤΙ ΤΟ ΒΛΕΠΕΙΣ ΕΣΥ, ΚΑΙ ΟΧΙ ΤΙ ΕΓΙΝΕ.**
                        Η φράση παραπάνω είναι **η ίδια για κάθε αναγνώστη**: ο
                        ίδιος γεγονότος-φάκελος αποδίδεται στη ροή της συλλογής,
                        στις ειδοποιήσεις και στο ιστορικό ενός πόρου. Χωρίς αυτή
                        τη σήμανση, ο αναφερόμενος θα διάβαζε «σχολίασε πόρο» —
                        ακριβώς ό,τι διαβάζει και όποιος ειδοποιήθηκε επειδή
                        συμμετέχει στο νήμα — και **δεν θα μάθαινε ποτέ ότι τον
                        φώναξαν ονομαστικά**.

                        Ο λόγος είναι ιδιότητα **του παραλήπτη** και όχι του
                        γεγονότος (D2), οπότε ο εξυπηρετητής στέλνει μόνο τον
                        **δικό μου**: κανείς δεν μαθαίνει ποιοι άλλοι
                        ειδοποιήθηκαν, ούτε γιατί. */}
                    {entry.reason === 'mention' && (
                        <span className="rounded-full bg-accent-soft px-2 py-0.5 text-xs font-semibold text-accent">
                            <i className="fa-solid fa-at me-1" aria-hidden="true" />
                            {MENTIONS.mentionedYou}
                        </span>
                    )}
                </div>

                {/* Το `summary` φέρει ό,τι περιγράφει τη **μεταβολή** και όχι την
                    τρέχουσα κατάσταση: την παλιά τιμή, και το πλήθος των κρίσεων
                    που διαγράφηκαν κατά τη D13. Είναι συχνά η μόνη θέση όπου
                    επιβιώνει το μέγεθος μιας μη αναστρέψιμης ενέργειας. */}
                {entry.summary && <p className="text-sm text-muted">{entry.summary}</p>}

                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    <TimeAgo date={entry.createdAt} className="text-xs text-muted" />
                    {entry.count > 1 && (
                        <span className="text-xs text-muted">{ACTIVITY.inSeveral(entry.count)}</span>
                    )}
                    {isUnread && onMarkRead && (
                        <span className="rounded-full bg-accent px-2 py-0.5 text-xs font-medium text-accent-foreground">
                            {ACTIVITY.unread}
                        </span>
                    )}
                </div>

                {/* **Το σφάλμα αποδίδεται ΔΙΠΛΑ στην ενέργεια**, μέσα στη γραμμή.
                    Ο κατάλογος έχει δική του κύλιση, οπότε μήνυμα στην κορυφή θα
                    ήταν έξω από το οπτικό πεδίο τη στιγμή που ο χρήστης πατά ένα
                    κουμπί στη μέση της λίστας — παγίδα που έχει ήδη κοστίσει. */}
                {error && (
                    <p className="text-sm text-danger" role="alert">
                        {error}
                    </p>
                )}
            </div>

            {isUnread && onMarkRead && (
                <Button
                    size="sm"
                    variant="ghost"
                    isPending={isMarking}
                    onPress={() => onMarkRead(entry)}
                    aria-label={ACTIVITY.markRead}
                    className="shrink-0 self-start"
                >
                    <i className="fa-solid fa-check" aria-hidden="true" />
                </Button>
            )}
        </li>
    );
}
